import React, { useState, useCallback, useRef } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  DropAnimation,
  MouseSensor,
  TouchSensor,
  defaultDropAnimationSideEffects,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { WorkflowItem, WorkflowStatus, columnIdOf } from '../../config';
import KanbanColumn from '../KanbanColumn';
import KanbanCardBody from '../KanbanCardBody';

// =======================================
// INTERFACES
// =======================================

interface KanbanBoardProps {
  statuses: WorkflowStatus[];
  /** The cards of each column, grouped and sorted (groupItemsByStatus). */
  itemsByStatus: Map<number, WorkflowItem[]>;
  collapsedIds: Set<number>;
  onToggleCollapsed: (statusId: number) => void;
  onStatusChange: (itemId: number, entityType: 'project' | 'stage', newStatusId: number) => void;
  onCardClick: (item: WorkflowItem) => void;
  /** Opens the Move to sheet. Absent for a viewer who cannot move cards. */
  onMoveRequest?: (item: WorkflowItem) => void;
  /** The horizontal scroller, for the column rail's scroll-spy and jumps. */
  boardRef: React.RefObject<HTMLDivElement | null>;
  isLoading?: boolean;
}

// The lifted card settles into its new place rather than vanishing.
const DROP_ANIMATION: DropAnimation = {
  duration: 180,
  easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
  sideEffects: defaultDropAnimationSideEffects({
    styles: { active: { opacity: '0.35' } },
  }),
};

// Edge auto-scroll while a card is held: sideways across the board, and up or
// down inside a long column.
const AUTO_SCROLL = {
  threshold: { x: 0.12, y: 0.15 },
  acceleration: 14,
};

// How long after a drag ends a click on a card is ignored. The browser fires
// its click after mouseup / touchend, by which time the drag state has
// already cleared, so the click cannot be told apart by looking at it.
const CLICK_AFTER_DRAG_MS = 400;

// =======================================
// MAIN COMPONENT
// =======================================

const KanbanBoard: React.FC<KanbanBoardProps> = ({
  statuses,
  itemsByStatus,
  collapsedIds,
  onToggleCollapsed,
  onStatusChange,
  onCardClick,
  onMoveRequest,
  boardRef,
  isLoading = false,
}) => {
  const [activeItem, setActiveItem] = useState<WorkflowItem | null>(null);

  // Mouse: a few pixels of travel, so a click still opens the card.
  // Touch: a deliberate long-press, so a swipe that starts on a card scrolls
  // the column or the board instead of picking the card up.
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: { distance: 4 },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { delay: 250, tolerance: 8 },
  });
  const sensors = useSensors(mouseSensor, touchSensor);

  // A drag that ends where it began (same column, or a long-press let go
  // without moving) is followed by the browser's click on the card. Without
  // this it opens the project the user was trying to move.
  const suppressClickUntil = useRef(0);
  const endDrag = useCallback(() => {
    setActiveItem(null);
    suppressClickUntil.current = Date.now() + CLICK_AFTER_DRAG_MS;
  }, []);

  const handleCardClick = useCallback((item: WorkflowItem) => {
    if (Date.now() < suppressClickUntil.current) return;
    onCardClick(item);
  }, [onCardClick]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const dragged = (event.active.data.current?.item as WorkflowItem | undefined) ?? null;
    setActiveItem(dragged);
    // A small tick on Android as a long-pressed card lifts. Touch only, and
    // only after a real tap (Chrome refuses and logs otherwise); a no-op where
    // unsupported, which includes iPad Safari.
    const isTouch = typeof TouchEvent !== 'undefined' && event.activatorEvent instanceof TouchEvent;
    // userActivation is newer than this TS lib's Navigator type.
    const activation = (navigator as Navigator & { userActivation?: { hasBeenActive: boolean } })
      .userActivation;
    if (isTouch && typeof navigator.vibrate === 'function' && activation?.hasBeenActive !== false) {
      navigator.vibrate(8);
    }
  }, []);

  // Every drop target is a column, so the only outcome is a status change.
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const dragged = event.active.data.current?.item as WorkflowItem | undefined;
    endDrag();
    const overId = event.over?.id;
    if (!dragged || typeof overId !== 'string' || !overId.startsWith('column-')) return;

    const newStatusId = Number(overId.slice('column-'.length));
    if (!Number.isFinite(newStatusId) || newStatusId === columnIdOf(dragged, statuses)) return;
    onStatusChange(dragged.id, dragged.entity_type, newStatusId);
  }, [endDrag, statuses, onStatusChange]);

  const classes = [
    'kanban-board',
    isLoading ? 'is-filtering' : '',
    activeItem ? 'is-dragging' : '',
  ].filter(Boolean).join(' ');

  return (
    <DndContext
      sensors={sensors}
      autoScroll={AUTO_SCROLL}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={endDrag}
    >
      <div ref={boardRef} className={classes}>
        {statuses.map(status => (
          <KanbanColumn
            key={status.id}
            status={status}
            items={itemsByStatus.get(status.id) ?? []}
            collapsed={collapsedIds.has(status.id)}
            onToggleCollapsed={onToggleCollapsed}
            onCardClick={handleCardClick}
            onMoveRequest={onMoveRequest}
          />
        ))}
      </div>

      {/* The lifted copy under the pointer: the same body as the card. */}
      <DragOverlay dropAnimation={DROP_ANIMATION}>
        {activeItem && (
          <div className="kanban-card is-overlay">
            <KanbanCardBody item={activeItem} />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
};

export default KanbanBoard;
